/**
 * Bubbles and items in play (spec 05 §6.4): a glass sphere per bubble - Bub's green, Bob's blue as
 * in the arcade, the holes' green (every one green in the 1987 look, as on the C64) - with what it
 * holds inside (a letter, a drop, a flame, a bolt; a trapped monster is drawn by EntityViews); growing
 * smoothly through the shot, flashing red where the C64 blinks it out at the end of its life (a glass
 * bubble vanishing and coming back reads as a glitch), swelling as it bursts. Items are their food
 * models, turning slowly. The aids (08 §4): with the colour aid a bubble about to burst wears a thick
 * broken rim and wobbles; with reduce flashing it turns red and stays red instead of flashing.
 */

/** How fast a bubble's drawn size follows the C64's three shot sizes and two burst pictures (1/s). */
const SIZE_RATE = 30;

import { type Material, Mesh, type Object3D, type Scene, SphereGeometry, Vector3 } from 'three';
import type { Bubble, Item, World } from '../../sim/types';
import { powerupName } from '../../sim/items/powerups';
import { spriteCentre } from '../../sim/coords';
import { bolt, flame, letterVoxels, waterDrop } from '../actors/bubbleContents';
import { foodModel } from '../actors/food';
import { powerupModel } from '../actors/powerups';
import type { MeshBuilder } from '../kit/MeshBuilder';
import { createGlassBubbleMaterial } from '../materials/glassBubble';
import type { Palette } from '../palette';
import type { FigureOptions, Previous } from './entityViews';
import { softFlash } from './flash';
import { WaterView } from './waterView';
import { foodName } from '../../sim/bubbles/food';
import { deathFlight } from '../../sim/bubbles/items';
import { bubbleLook } from '../../sim/bubbles/view';
import { centreToWorld, lerpCoord, pixelToWorld } from './simToWorld';
import { EXTEND_FRAMES } from '../../sim/tuning';
import { EXTEND_FOOD_FRAMES } from '../../sim/bubbles';

const LETTERS = 'EXTEND';

/** The giant bonus items (02 §7.6), by the POWERUPS index of the staff or box that set them up. */
const GIANT_FOODS: Readonly<Record<number, string>> = {
  1: 'cupcake', 2: 'watermelon', 21: 'cupcake', 23: 'watermelon', 25: 'yellow diamond', 26: 'blue diamond', 27: 'purple diamond',
};
/** How much bigger than an ordinary item a giant one is drawn (the C64's are 2x2 sprites). */
const GIANT_SCALE = 2.6;

/** An item's model: a power-up by its table name, a giant item by what it drops as, the rest by food. */
function itemModel(i: Item, p: Palette): MeshBuilder {
  if (i.kind === 'powerup') return powerupModel(powerupName(i.item), p);
  if (i.kind === 'giant') return foodModel(GIANT_FOODS[i.item] ?? 'purple diamond', p);
  return foodModel(foodName(i.item), p);
}

type Glass = 'bub' | 'bob' | 'hole' | 'special' | 'warn';

/** How fast a bubble about to burst wobbles with the colour aid on (under the flash cap), and how far. */
const WOBBLE_HZ = 2.5;
const WOBBLE = 0.07;

interface BubbleView {
  shell: Mesh;
  /** Its own glass (it flashes to the warning glass). */
  glass: Glass;
  /** It has begun its end-of-life blink: it bursts within the second. */
  warned: boolean;
  inside: Object3D | null;
  kind: string;
  /** The size drawn, easing toward the C64's. */
  shown: number;
}

export class BubbleViews {
  private readonly sphere = new SphereGeometry(0.9, 40, 28);
  private readonly glass: Record<Glass, Material>;
  /** The same glasses with the colour aid's broken rim (the same program: nothing compiles when they appear). */
  private readonly aidGlass: Record<Glass, Material>;
  options: FigureOptions = { colourAid: false, reduceFlashing: false };
  private lastT = -1;
  private readonly bubbles = new Map<number, BubbleView>();
  private readonly items = new Map<number, Mesh>();
  private readonly tmp = new Vector3();
  private readonly water: WaterView;
  /** Bub's and Bob's bubbles while they fly in after a scroll (02 §9.1). */
  private readonly flyShells: [Mesh, Mesh];

  /**
   * `c64`: the 1987 look - every bubble the C64's green, not Bob's blue as in the arcade (and the glass
   * clear in the middle: `glassBody`, set by GameView).
   */
  constructor(private readonly scene: Scene, private readonly p: Palette, private readonly contentMat: Material, c64 = false) {
    this.water = new WaterView(scene, p);
    const set = (brokenRim: boolean): Record<Glass, Material> => {
      const glass = (ramp: string): Material =>
        createGlassBubbleMaterial({ tint: p.color(`${ramp}.1`), rim: p.color(`${ramp}.2`), brokenRim });
      const blue = c64 ? 'bubble' : 'bob';
      return { bub: glass('bubble'), bob: glass(blue), hole: glass('bubble'), special: glass(blue), warn: glass('bubbleLate') };
    };
    this.glass = set(false);
    this.aidGlass = set(true);
    this.flyShells = [new Mesh(this.sphere, this.glass.bub), new Mesh(this.sphere, this.glass.bob)];
    for (const s of this.flyShells) {
      s.visible = false;
      s.scale.setScalar(1.25);
      scene.add(s);
    }
  }

  private contents(b: Bubble): Object3D | null {
    const c = b.content;
    let mb = null;
    if (c.kind === 'letter') mb = letterVoxels(this.p, LETTERS[c.letter] ?? 'E');
    else if (c.kind === 'water') mb = waterDrop(this.p);
    else if (c.kind === 'fire') mb = flame(this.p);
    else if (c.kind === 'lightning') mb = bolt(this.p);
    return mb ? new Mesh(mb.build(), this.contentMat) : null;
  }

  private view(b: Bubble): BubbleView {
    let v = this.bubbles.get(b.id);
    if (!v || v.kind !== b.content.kind) {
      if (v) this.drop(b.id, v);
      const special = b.content.kind !== 'empty' && b.content.kind !== 'monster';
      const glass: Glass = special ? 'special' : b.owner === 1 ? 'bob' : b.owner === 0 ? 'bub' : 'hole';
      v = {
        shell: new Mesh(this.sphere, this.glass[glass]), glass, warned: false, inside: this.contents(b), kind: b.content.kind, shown: -1,
      };
      this.scene.add(v.shell);
      if (v.inside) this.scene.add(v.inside);
      this.bubbles.set(b.id, v);
    }
    return v;
  }

  private drop(id: number, v: BubbleView): void {
    this.scene.remove(v.shell);
    if (v.inside) {
      this.scene.remove(v.inside);
      if (v.inside instanceof Mesh) v.inside.geometry.dispose();
    }
    this.bubbles.delete(id);
  }

  /**
   * A bubble where the C64 draws it (`bubbleLook`): small, medium or full through the shot; the
   * two burst pictures as a swelling, thinning shell; nothing while blinked or flashed off.
   */
  private syncBubble(b: Bubble, prev: Previous, alpha: number, dt: number, t: number): void {
    const v = this.view(b);
    const look = bubbleLook(b);
    const before = prev.get(`u${b.id}`) ?? look;
    centreToWorld(lerpCoord(before.x, look.x, alpha), lerpCoord(before.y, look.y, alpha), this.tmp);
    const { colourAid, reduceFlashing } = this.options;
    // The burst swells the shell (a softer swell with reduce flashing).
    const size = look.burst >= 0 ? 1 + softFlash(0.2 + look.burst * 0.25, reduceFlashing) : [0.45, 0.72, 1][look.size] ?? 1;
    v.shown = v.shown < 0 ? size : v.shown + (size - v.shown) * (1 - Math.exp(-dt * SIZE_RATE));
    if (look.blink) v.warned = true;
    const drawn = look.visible || look.blink;
    v.shell.position.copy(this.tmp);
    const aid = colourAid && v.warned && look.burst < 0;
    const wobble = aid ? WOBBLE * Math.sin(t * Math.PI * 2 * WOBBLE_HZ) : 0;
    v.shell.scale.set(v.shown * (1 + wobble), v.shown * (1 - wobble), v.shown);
    v.shell.visible = drawn;
    // The C64 blinks it out every other pair of passes (about 4 Hz): a flash to red here; with reduce
    // flashing, red from the first blink on.
    const glass = look.blink || (reduceFlashing && v.warned) ? 'warn' : v.glass;
    v.shell.material = (aid ? this.aidGlass : this.glass)[glass];
    if (v.inside) {
      v.inside.position.copy(this.tmp);
      v.inside.visible = drawn && look.burst < 0;
    }
  }

  /** An item: its food model once it has landed (while it flies, EntityViews draws the dead monster). */
  private syncItem(i: Item, prev: Previous, alpha: number, t: number, hidden: boolean): void {
    const flight = deathFlight(i);
    if (hidden) {
      const m = this.items.get(i.id);
      if (m) m.visible = false;
      return;
    }
    if (flight && flight.stage !== 'item') {
      const m = this.items.get(i.id);
      if (m) m.visible = false;
      return;
    }
    let mesh = this.items.get(i.id);
    if (!mesh) {
      mesh = new Mesh(itemModel(i, this.p).build(), this.contentMat);
      this.scene.add(mesh);
      this.items.set(i.id, mesh);
    }
    const before = prev.get(`i${i.id}`) ?? i;
    const x = lerpCoord(before.x, i.x, alpha);
    const y = lerpCoord(before.y, i.y, alpha);
    mesh.visible = true;
    if (i.kind === 'giant') {
      // Its (x, y) is the top-left sprite of a 2x2 block (48 x 42 px): it stands on the block's bottom middle.
      pixelToWorld(x - 24 + 24, y - 50 + 42, mesh.position);
      mesh.scale.setScalar(1.35 * GIANT_SCALE);
    } else {
      centreToWorld(x, y, this.tmp);
      mesh.position.set(this.tmp.x, this.tmp.y - 0.95, 0);
      mesh.scale.setScalar(1.35);
    }
    mesh.rotation.y = Math.sin(t * 2 + i.id) * 0.5;
  }

  /** The fly-in: each player in the game rides a bubble of its colour to its start. */
  private syncFlyIn(world: World, prev: Previous, alpha: number): void {
    const flying = world.flow.phase === 'flyIn';
    for (const id of [0, 1] as const) {
      const shell = this.flyShells[id];
      const p = world.players[id];
      shell.visible = flying && p.state === 'play';
      if (!shell.visible) continue;
      const before = prev.get(`p${id}`) ?? p;
      const c = spriteCentre(lerpCoord(before.x, p.x, alpha), lerpCoord(before.y, p.y, alpha));
      shell.position.set(c.x / 8 - 16, 12.5 - c.y / 8, 0);
    }
  }

  /** `alpha`: through the sprite (I-tick) interval, for items; `softAlpha`: the M-tick one, for bubbles. */
  sync(world: World, prev: Previous, alpha: number, softAlpha: number, t: number): void {
    this.syncFlyIn(world, prev, alpha);
    const dt = this.lastT < 0 ? 0 : Math.min(0.1, Math.max(0, t - this.lastT));
    this.lastT = t;
    this.water.sync(world, t);
    const seen = new Set<number>();
    for (const b of world.bubbles) {
      seen.add(b.id);
      this.syncBubble(b, prev, softAlpha, dt, t);
    }
    for (const [id, v] of this.bubbles) if (!seen.has(id)) this.drop(id, v);
    const items = new Set<number>();
    // EXTEND's screenful of food (02 §7.7) shows only once the fanfare's scrambled letters are over.
    const f = world.flow;
    const extendFoodHidden = f.extendFrames > 0 && EXTEND_FRAMES - f.extendFrames < EXTEND_FOOD_FRAMES;
    for (const i of world.items) {
      items.add(i.id);
      this.syncItem(i, prev, alpha, t, extendFoodHidden && i.kind === 'bonus');
    }
    for (const [id, mesh] of this.items) {
      if (items.has(id)) continue;
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      this.items.delete(id);
    }
  }

  /** Every material a bubble, its contents, an item or the water can be drawn with (for warming up). */
  materials(): Material[] {
    return [...Object.values(this.glass), ...Object.values(this.aidGlass), this.contentMat, this.water.material];
  }

  /** Take everything out of the scene and free what it made (the contents' material is the caller's). */
  dispose(): void {
    this.clear();
    this.water.dispose();
    this.water.material.dispose();
    this.scene.remove(...this.flyShells);
    this.sphere.dispose();
    for (const m of [...Object.values(this.glass), ...Object.values(this.aidGlass)]) m.dispose();
  }

  /** Drop every bubble and item. */
  clear(): void {
    this.water.clear();
    for (const [id, v] of this.bubbles) this.drop(id, v);
    for (const mesh of this.items.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    this.items.clear();
  }
}
